// Builds a tidy, one-row-per-touch CSV of match events suitable for handing to
// an AI model (e.g. Claude) for deep analysis. Handles both DataVolley (.dvw)
// events (which carry a `dvString` scout code and `DVGrade`) and VBStats/PSVB
// events (which use numeric `EventGrade`/`SubEvent`).
//
// Columns:
//   Team, Match, Set, Rotation, Player, Player_Role, Skill, Skill_Type,
//   Type_of_Hit, Attack_Combo, Number_of_Blocks, Setter_Call, Evaluation,
//   Zone, Start_Zone, End_Zone

import { getRotationLabels } from "./Constants";
import {
    buildSectionStatTables,
    kStatTableLegend,
} from "./DeepAnalysisStats";
import { zoneFromString } from "./Utils";

// Skill names by EventType.
const kSkillNames = {
  1: "Serve",
  2: "Reception",
  3: "Set",
  4: "Spike",
  5: "Block",
  6: "Dig",
  7: "Cover",
  20: "Setter Call",
};

// Real skill events worth exporting (ignore points/timeouts/substitutions etc.).
const kExportSkills = [1, 2, 3, 4, 5, 6, 7];

// DataVolley player roles, indexed by the value stored in player.Positions.
const kRoleNames = [
  "",
  "Libero",
  "Pass/Hitter",
  "Opposite",
  "Middle",
  "Setter",
];

// VBStats serve types, indexed by SubEvent.
const kPSVBServeTypes = ["", "Jump", "Jump-Float", "Float", "Topspin"];
// VBStats attack hit types, indexed by SubEvent.
const kPSVBHitTypes = ["", "Off-speed", "Tip", "Power", "Off-Block"];

// DataVolley serve type code (scout char at index 4) -> friendly name.
const kDVServeTypes = {
  H: "Jump (Topspin)",
  M: "Jump-Float",
  T: "Float",
  Q: "Float",
  O: "Other",
  N: "Other",
};

// DataVolley attack tempo code (scout char at index 4) -> friendly name.
const kDVAttackTempo = {
  H: "High",
  M: "Medium",
  Q: "Quick",
  T: "Tense",
  U: "Super",
  F: "Fast",
  O: "Other",
  N: "Other",
};

// DataVolley attack type code (scout char at index 12) -> friendly name.
const kDVHitTypes = {
  H: "Hard",
  P: "Tip",
  T: "Tip",
  S: "Soft",
  O: "Other",
};

// Returns the first non-empty positional code char of a DataVolley scout string.
const dvChar = (e, idx) => {
  if (!e.dvString) {
    return "";
  }
  const token = e.dvString.split(";")[0];
  if (token.length > idx) {
    const c = token.charAt(idx);
    return c === "~" || c === " " ? "" : c;
  }
  return "";
};

const isDV = (e) => e.DVGrade !== undefined;

// Normalised team name used to decide which team is common across matches.
const teamName = (team) => (team && team.Name ? String(team.Name).trim() : "");

// When several matches are loaded, the team that appears in EVERY match is the
// tracked team. Returns its normalised name, or null if there is no single team
// common to all matches (or only one match is loaded).
export function findCommonTeamName(matches) {
  if (!matches || matches.length < 2) {
    return null;
  }
  const pairs = matches.map((m) => [teamName(m.teamA), teamName(m.teamB)]);
  const candidates = pairs[0].filter(Boolean);
  for (const cand of candidates) {
    if (pairs.every((pair) => pair.includes(cand))) {
      return cand;
    }
  }
  return null;
}

// Which side ("A" or "B") of a single match is the common (tracked) team.
// Returns null when there is no common team, so single matches keep their
// natural home (Team A) / away (Team B) assignment.
export function commonSideForMatch(match, commonName) {
  if (!commonName) {
    return null;
  }
  if (teamName(match.teamA) === commonName) {
    return "A";
  }
  if (teamName(match.teamB) === commonName) {
    return "B";
  }
  return null;
}

// The raw side ("A" = home/first team, "B" = away) an event belongs to.
const rawEventSide = (e, match) => {
  if (e.Player && e.Player.Guid) {
    if (match.teamA.players.some((p) => p.Guid === e.Player.Guid)) {
      return "A";
    }
    if (match.teamB.players.some((p) => p.Guid === e.Player.Guid)) {
      return "B";
    }
  }
  if (e.dvString && e.dvString.length > 0) {
    return e.dvString.charAt(0) === "*" ? "A" : "B";
  }
  if (e.homePlayer !== undefined) {
    return e.homePlayer ? "A" : "B";
  }
  return "";
};

// Which team an event belongs to: "Team A" or "Team B". When `commonSide` is
// provided (multiple matches with a single tracked team), the common team is
// always labelled "Team A" and every opponent is "Team B"; otherwise the home
// team is "Team A" and the away team is "Team B".
const eventTeam = (e, match, commonSide) => {
  const side = rawEventSide(e, match);
  if (!side) {
    return "";
  }
  if (commonSide) {
    return side === commonSide ? "Team A" : "Team B";
  }
  return side === "A" ? "Team A" : "Team B";
};

const playerRole = (pl) => {
  if (!pl || !pl.Positions || pl.Positions.length === 0) {
    return "";
  }
  const idx = pl.Positions[0];
  return idx >= 0 && idx < kRoleNames.length ? kRoleNames[idx] : "";
};

// Normalised evaluation symbol (=, /, -, !, +, #). DataVolley already provides
// one; for VBStats we approximate from the numeric grade.
const evaluationOf = (e) => {
  if (isDV(e)) {
    return e.DVGrade || "";
  }
  const g = e.EventGrade;
  if (g === 0) return "=";
  if (g === 1) return "-";
  if (g === 2) return "!";
  if (g === 3) return "#";
  return "";
};

const skillTypeOf = (e) => {
  if (e.EventType === 1) {
    // Serve
    if (isDV(e)) {
      const c = dvChar(e, 4);
      return kDVServeTypes[c] || c;
    }
    return kPSVBServeTypes[e.SubEvent] || "";
  }
  if (e.EventType === 4) {
    // Spike tempo
    if (isDV(e)) {
      const c = dvChar(e, 4);
      return kDVAttackTempo[c] || c;
    }
  }
  return "";
};

const hitTypeOf = (e) => {
  if (e.EventType !== 4) {
    return "";
  }
  if (isDV(e)) {
    const c = dvChar(e, 12);
    return kDVHitTypes[c] || c;
  }
  return kPSVBHitTypes[e.SubEvent] || "";
};

// Number of blockers on an attack.
const numberOfBlocksOf = (e) => {
  if (e.EventType !== 4) {
    return "";
  }
  if (isDV(e)) {
    return e.NumberOfBlocks || "";
  }
  if (e.SubEvent2 && e.SubEvent2 !== 0) {
    return `${e.SubEvent2 - 1}B`;
  }
  return "";
};

// The court zone of the contact. DataVolley encodes a start zone in the scout
// code; otherwise fall back to the coordinate-derived ball start zone.
const zoneOf = (e) => {
  if (isDV(e)) {
    const c = dvChar(e, 9);
    if (c) {
      return c;
    }
  }
  const z = zoneFromString(e.BallStartString);
  return z > 0 ? z.toString() : "";
};

const startZoneOf = (e) => {
  const z = zoneFromString(e.BallStartString);
  return z > 0 ? z.toString() : "";
};

const endZoneOf = (e) => {
  const z = zoneFromString(e.BallEndString);
  return z > 0 ? z.toString() : "";
};

const csvEscape = (value) => {
  const s = value == null ? "" : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export const kMatchCSVColumns = [
  "Team",
  "Match",
  "Set",
  "Rotation",
  "Player",
  "Player_Role",
  "Skill",
  "Skill_Type",
  "Type_of_Hit",
  "Attack_Combo",
  "Number_of_Blocks",
  "Setter_Call",
  "Evaluation",
  "Zone",
  "Start_Zone",
  "End_Zone",
];

// Builds an array of normalised, one-object-per-touch event rows. This is the
// single source of truth consumed by both the CSV export and the pre-computed
// stat-table builder (DeepAnalysisStats.js). `selectedGame` of 0 (or undefined)
// includes every set; otherwise only that set number is included. Each row's
// keys match `kMatchCSVColumns`. The row also carries a non-exported `Row`
// (1-6 rotation index) for downstream grouping.
export function buildMatchEventRows(matches, selectedGame) {
  if (!matches || matches.length === 0) {
    return [];
  }

  const rotationLabels = getRotationLabels();
  const out = [];

  // Across multiple matches, the team present in every match is the tracked
  // team and is always labelled "Team A"; all opponents become "Team B".
  const commonName = findCommonTeamName(matches);

  matches.forEach((match, mi) => {
    const commonSide = commonSideForMatch(match, commonName);
    const events = match.events || [];
    for (const e of events) {
      if (!kExportSkills.includes(e.EventType)) {
        continue;
      }
      if (!e.Player) {
        continue;
      }
      const setNo = e.Drill ? e.Drill.GameNumber : "";
      if (selectedGame && selectedGame !== 0 && setNo !== selectedGame) {
        continue;
      }
      const rotation =
        e.Row > 0 && e.Row <= rotationLabels.length
          ? rotationLabels[e.Row - 1]
          : "";
      out.push({
        Team: eventTeam(e, match, commonSide),
        Match: mi + 1,
        Set: setNo,
        Rotation: rotation,
        Player: e.Player.shirtNumber,
        Player_Role: playerRole(e.Player),
        Skill: kSkillNames[e.EventType] || "",
        Skill_Type: skillTypeOf(e),
        Type_of_Hit: hitTypeOf(e),
        Attack_Combo: e.attackCombo || "",
        Number_of_Blocks: numberOfBlocksOf(e),
        Setter_Call: e.settersCall || "",
        Evaluation: evaluationOf(e),
        Zone: zoneOf(e),
        Start_Zone: startZoneOf(e),
        End_Zone: endZoneOf(e),
        Row: e.Row || 0,
      });
    }
  });

  return out;
}

// Builds the CSV string. `selectedGame` of 0 (or undefined) exports every set;
// otherwise only that set number is exported. Returns { csv, rowCount }.
export function buildMatchEventsCSV(matches, selectedGame) {
  const rowObjs = buildMatchEventRows(matches, selectedGame);
  if (rowObjs.length === 0) {
    return { csv: "", rowCount: 0 };
  }

  const lines = [kMatchCSVColumns.join(",")];
  for (const r of rowObjs) {
    lines.push(kMatchCSVColumns.map((c) => csvEscape(r[c])).join(","));
  }

  return { csv: lines.join("\n"), rowCount: rowObjs.length };
}

// Triggers a browser download of the given CSV text.
export function downloadCSV(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "match_events.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// A sensible CSV file name for the loaded match(es).
export function matchCSVFileName(matches) {
  if (matches && matches.length === 1) {
    return `${matches[0].teamA.Name}_vs_${matches[0].teamB.Name}_events.csv`.replace(
      /\s+/g,
      "_"
    );
  }
  return "match_events.csv";
}

// Human-readable description of each CSV column, sent to the AI so it can
// interpret the data correctly.
export const kCsvColumnLegend = [
  "Team — which team performed the touch. With multiple matches, Team A is the tracked team that appears in every match and Team B is each opponent; with a single match, Team A is the home/first team and Team B the away team.",
  "Match — match number (1-based) when multiple matches are loaded.",
  "Set — set number within the match.",
  "Rotation — the performing team's rotation label.",
  "Player — player shirt number.",
  "Player_Role — Setter, Opposite, Middle, Pass/Hitter (outside) or Libero.",
  "Skill — Serve, Reception, Set, Spike, Block, Dig or Cover.",
  "Skill_Type — serve type (Jump, Jump-Float, Float, Topspin) or attack tempo (Quick, Medium, High, Fast...).",
  "Type_of_Hit — for spikes: Hard, Tip, Soft, Off-speed, Off-Block.",
  "Attack_Combo — DataVolley attack combination code (e.g. X5, V5) for spikes/sets.",
  "Number_of_Blocks — number of blockers on the attack (e.g. B0, B1, B2, B3).",
  "Setter_Call — setter call code when set.",
  "Evaluation — outcome symbol: # = perfect/point (kill, ace, perfect pass, stuff block), + = positive/good, ! = ok/medium, - = poor/negative, / = poor return/overpass (or attack blocked for reattack), = = error/point lost.",
  "Zone — court zone (1-9) of the contact.",
  "Start_Zone — court zone the ball travelled from (1-9).",
  "End_Zone — court zone the ball travelled to / landed in (1-9).",
];

// The deep-analysis brief, split into independent sections so each can be run
// on its own (saving AI credits). Each section has a stable `key`, a display
// `title` (used as the Markdown heading and the section label in the UI) and a
// default list of `questions`. Users may edit/add to these questions in the UI
// before submitting; the defaults below are only the starting point.
export const kDeepAnalysisSections = [
  {
    key: "serving",
    title: "Serving",
    questions: [
      "Serve efficiency (aces, errors, and pressured receptions forced).",
      "Which servers are most effective, and against which reception zones?",
      "Which serve types (jump, float, jump-float) yield the best results?",
    ],
  },
  {
    key: "reception",
    title: "Reception",
    questions: [
      "Reception quality breakdown (perfect, positive, negative, error).",
      "Which players struggle most under jump serves vs. float serves?",
      "How does poor reception correlate with side-out efficiency?",
    ],
  },
  {
    key: "setting",
    title: "Setting",
    questions: [
      "Set distribution by zone and by player.",
      "How does the setter perform under pressure (bad-pass situations)?",
      "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.",
    ],
  },
  {
    key: "attack",
    title: "Attack",
    questions: [
      "Kill percentage and error rate per player, per rotation, and per zone.",
      "Which attack zones (pipe, cross, line) have the highest efficiency?",
      "How attack efficiency changes in transition vs. side-out.",
    ],
  },
  {
    key: "blocking",
    title: "Blocking",
    questions: [
      "Block-per-set rate per player and rotation.",
      "Which opposing attack zones are most successfully blocked?",
      "How often blocks result in kills vs. touch-outs.",
    ],
  },
  {
    key: "defence",
    title: "Defence / Digging",
    questions: [
      "Dig success rate per player and per court zone.",
      "Which attack types (tip, roll, hard swing) are hardest to defend?",
      "How defensive efficiency affects transition attack success.",
    ],
  },
  {
    key: "rotation",
    title: "Rotation Analysis",
    questions: [
      "Which rotations have the highest and lowest point differential?",
      "Side-out and break-point percentages per rotation.",
      "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).",
    ],
  },
  {
    key: "opponent",
    title: "Opponent Tendencies (Scouting)",
    questions: [
      "The opponent's most frequently used attack zones and combinations.",
      "The opponent's go-to attackers in high-pressure situations.",
      "The serve-receive patterns the opponent favours.",
    ],
  },
  {
    key: "overall",
    title: "Overall Team Metrics",
    questions: [
      "Overall rally-win probability in different score situations.",
      "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).",
      "The correlation between first-ball side-out percentage and match outcome.",
    ],
  },
];

// Look up a section definition by its key.
export const deepAnalysisSection = (key) =>
  kDeepAnalysisSections.find((s) => s.key === key);

// Shared intro lines (system framing + pre-computed stat tables) used by every
// deep-analysis prompt. `dataBlock` is the Markdown stat tables produced by
// buildSectionStatTables (pre-aggregated to save tokens), `legend` the short
// how-to-read note.
function deepAnalysisIntro(matches, selectedGame, selectedTeam, dataBlock, legend) {
  const selectedTeamLabel = selectedTeam === 0 ? "Team A" : "Team B";
  const opponentTeamLabel = selectedTeam === 0 ? "Team B" : "Team A";
  const scope =
    !selectedGame || selectedGame === 0
      ? matches.length > 1
        ? `${matches.length} matches`
        : "the full match"
      : `set ${selectedGame}`;
  const multiMatch = matches.length > 1;
  const teamNote = multiMatch
    ? "Team A is the tracked team that plays in every match; Team B aggregates the various opponents across those matches."
    : "Teams are anonymised as Team A and Team B; do not invent real names.";

  return {
    selectedTeamLabel,
    opponentTeamLabel,
    lines: [
      `You are an expert volleyball performance analyst and scout. You are given pre-computed statistical tables summarising ${scope}.`,
      `Analyse ${selectedTeamLabel}. Treat ${opponentTeamLabel} as the opponent (used for the scouting questions).`,
      teamNote,
      "Do not invent real team names.",
      "",
      ...legend.map((l) => `- ${l}`),
      "",
      "Pre-computed statistics:",
      dataBlock,
      "",
    ],
  };
}

// Core builder: produces a deep-analysis prompt for an array of sections. Each
// section is `{ key?, title, questions: [string] }` (questions already edited by
// the user). Empty questions are dropped. The prompt embeds pre-computed stat
// tables for the requested sections (instead of the full per-touch CSV) to keep
// the token count low. Returns null when there is no event data or no questions.
export function buildDeepAnalysisFromSections(
  matches,
  selectedGame,
  selectedTeam,
  sections
) {
  // Resolve the section keys we need stat tables for. Fall back to matching by
  // title when a caller passes sections without a key.
  const keys = (sections || [])
    .map((s) => {
      if (!s) {
        return null;
      }
      if (s.key) {
        return s.key;
      }
      const match = kDeepAnalysisSections.find((d) => d.title === s.title);
      return match ? match.key : null;
    })
    .filter(Boolean);

  const dataBlock = buildSectionStatTables(
    matches,
    selectedGame,
    selectedTeam,
    keys
  );
  // No event rows at all means there is genuinely nothing to analyse, so the
  // panel should show its not-ready message. If rows DO exist but this section
  // simply has no coded data (e.g. "Setting" when sets weren't coded), still
  // build the prompt with a note so the AI reports the gap rather than the
  // panel blocking the whole request.
  if (buildMatchEventRows(matches, selectedGame).length === 0) {
    return null;
  }
  const statsBlock = dataBlock
    ? dataBlock
    : "No data was coded for the requested aspect(s) in this match. State this clearly and do not invent figures.";

  const sectionBlocks = [];
  (sections || []).forEach((section) => {
    if (!section || !section.title) {
      return;
    }
    const qs = (section.questions || [])
      .map((q) => (typeof q === "string" ? q.trim() : ""))
      .filter((q) => q.length > 0)
      .map((q) => `- ${q}`);
    if (qs.length === 0) {
      return;
    }
    sectionBlocks.push(`### ${section.title}`);
    sectionBlocks.push(...qs);
  });
  if (sectionBlocks.length === 0) {
    return null;
  }

  const { selectedTeamLabel, lines } = deepAnalysisIntro(
    matches,
    selectedGame,
    selectedTeam,
    statsBlock,
    kStatTableLegend
  );
  const rotationLabels = getRotationLabels();
  const single = sectionBlocks.filter((l) => l.startsWith("### ")).length === 1;
  const focus = single
    ? `Focus exclusively on this aspect of ${selectedTeamLabel}'s play and answer every question below. `
    : `Work through the following analysis for ${selectedTeamLabel}. Answer every question, grouping your response under the same section headings. `;

  return [
    ...lines,
    focus +
      "Use Markdown with `##`/`###` headings, `**bold**` for key figures, bullet lists and small " +
      "tables where helpful. Base every statement on the pre-computed statistics above and cite concrete numbers " +
      "(counts, percentages, zones, rotations, player numbers). When the data is insufficient to " +
      "answer a point, say so briefly rather than inventing figures. Refer to rotations by their " +
      `labels as they appear in the tables (${rotationLabels.join(", ")}).`,
    "",
    ...sectionBlocks,
  ].join("\n");
}

// Builds a deep-analysis prompt for a single section.
export function buildDeepAnalysisSectionPrompt(
  matches,
  selectedGame,
  selectedTeam,
  section
) {
  return buildDeepAnalysisFromSections(matches, selectedGame, selectedTeam, [
    section,
  ]);
}

// Builds the full deep-analysis prompt (CSV + every default section) for the
// chosen team. `selectedTeam` is 0 for Team A, 1 for Team B. Returns null when
// there is no event data to analyse.
export function buildDeepAnalysisPrompt(matches, selectedGame, selectedTeam) {
  return buildDeepAnalysisFromSections(
    matches,
    selectedGame,
    selectedTeam,
    kDeepAnalysisSections
  );
}
